import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Box } from '@mui/material'
import { routes } from '@/router'
import { MedicalStatus, UserProfile } from '@/types'
import { sessionButtonSx } from '../session-button'
import { SessionField, SessionOption, SessionScreen } from '../session-screen'
import { MEDICAL_SITUATIONS } from './profile.constants'
import {
  flowProgress,
  nextProfileStep,
  previousProfileStep,
  setNoCrmChoice,
} from './profile-steps'
import { ProfileStepFrame } from './profile-step-frame'
import { saveProfile } from './save-profile'

export function ProfileSituationPage() {
  return (
    <ProfileStepFrame step="situation">
      {(user) => <SituationStep user={user} />}
    </ProfileStepFrame>
  )
}

function SituationStep({ user }: { user: UserProfile }) {
  const navigate = useNavigate()
  const [situation, setSituation] = useState<MedicalStatus | ''>(
    user.profile?.medical_status ?? '',
  )
  const [other, setOther] = useState(user.profile?.medical_status_other ?? '')
  const [saving, setSaving] = useState(false)

  const isValid =
    Boolean(situation) && (situation !== 'other' || other.trim().length > 2)
  const previous = previousProfileStep('situation', user)

  async function continueSituation() {
    if (!situation || !isValid || saving) return
    setSaving(true)

    try {
      const refreshed = await saveProfile({
        practice_area: 'Medicina',
        occupation:
          situation === 'enrolled_brazil'
            ? 'student'
            : situation === 'graduated_no_crm'
              ? 'doctor'
              : 'other',
        medical_status: situation,
        ...(situation === 'other'
          ? { medical_status_other: other.trim() }
          : {}),
      })
      setNoCrmChoice(false)
      const step = nextProfileStep(refreshed)
      navigate(step ? routes.session.profile[step] : routes.home)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SessionScreen
      title="Em qual situação você se encontra?"
      progress={flowProgress('situation', user)}
      onBack={() =>
        navigate(
          previous
            ? routes.session.profile[previous]
            : routes.session.profile.crm,
        )
      }
      footer={
        <LoadingButton
          size="large"
          fullWidth
          loading={saving}
          disabled={!isValid}
          onClick={continueSituation}
          sx={sessionButtonSx()}
        >
          Continuar
        </LoadingButton>
      }
    >
      <Box
        role="radiogroup"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
      >
        {MEDICAL_SITUATIONS.map((item) => (
          <SessionOption
            key={item.id}
            selected={situation === item.id}
            onClick={() => setSituation(item.id)}
          >
            {item.label}
          </SessionOption>
        ))}
      </Box>
      {situation === 'other' && (
        <SessionField
          id="situation-other"
          label="Conte pra gente qual é a sua situação"
          multiline
          minRows={3}
          value={other}
          placeholder="Ex.: Estudo fora do Brasil, sou pai do estudante…"
          onChange={(event) => setOther(event.target.value)}
          autoFocus
        />
      )}
    </SessionScreen>
  )
}
